/**
 * Forging System Configuration
 * MapleStory-style scroll system with colored gemstones
 * Failed scrolls permanently destroy a slot
 */

// ============================================================
// CONFIGURATION
// ============================================================

export const FORGING_CONFIG = {
  // Max gem slots by equipment slot type
  maxGemSlots: {
    weapon: 7,
    top: 9,
    bottom: 9,
    helmet: 7,
    gloves: 7,
    boots: 7,
    cape: 7,
    belt: 7,
    offhand: 7,
    pendant1: 5,
    pendant2: 5,
    ring1: 5,
    ring2: 5,
  },

  // Success rates by scroll tier (1-5)
  successRates: {
    1: 1.00,  // Tier 1 - 100%
    2: 0.70,  // Tier 2 - 70%
    3: 0.60,  // Tier 3 - 60%
    4: 0.30,  // Tier 4 - 30%
    5: 0.10,  // Tier 5 - 10%
  },

  // Base stat bonuses by scroll tier
  statBonuses: {
    1: { attack: 1, defense: 1, health: 10, critChance: 0.01, speed: 1 },
    2: { attack: 2, defense: 2, health: 25, critChance: 0.02, speed: 1 },
    3: { attack: 4, defense: 4, health: 50, critChance: 0.03, speed: 2 },
    4: { attack: 7, defense: 7, health: 100, critChance: 0.05, speed: 3 },
    5: { attack: 12, defense: 12, health: 200, critChance: 0.08, speed: 4 },
  },

  // Gemstone costs per scroll tier
  gemstoneCosts: {
    1: 1,
    2: 3,
    3: 8,
    4: 20,
    5: 50,
  },
};

// ============================================================
// GEMSTONES
// ============================================================

export const GEMSTONES = {
  ruby: {
    id: 'ruby',
    name: 'Ruby',
    icon: '🔴',
    color: '#e74c3c',
    stat: 'attack',
    description: 'A fiery red gemstone. Used to forge Attack scrolls.',
  },
  sapphire: {
    id: 'sapphire',
    name: 'Sapphire',
    icon: '🔵',
    color: '#3498db',
    stat: 'defense',
    description: 'A deep blue gemstone. Used to forge Defense scrolls.',
  },
  emerald: {
    id: 'emerald',
    name: 'Emerald',
    icon: '🟢',
    color: '#2ecc71',
    stat: 'health',
    description: 'A vibrant green gemstone. Used to forge Health scrolls.',
  },
  topaz: {
    id: 'topaz',
    name: 'Topaz',
    icon: '🟡',
    color: '#f1c40f',
    stat: 'critChance',
    description: 'A golden gemstone. Used to forge Critical scrolls.',
  },
  amethyst: {
    id: 'amethyst',
    name: 'Amethyst',
    icon: '🟣',
    color: '#9b59b6',
    stat: 'speed',
    description: 'A purple gemstone. Used to forge Speed scrolls.',
  },
};

// Gemstone IDs for iteration
export const GEMSTONE_IDS = ['ruby', 'sapphire', 'emerald', 'topaz', 'amethyst'];

// ============================================================
// SCROLL TYPES (5 stats x 5 tiers = 25 scrolls)
// ============================================================

export const SCROLL_TYPES = {
  // Attack Scrolls (use Ruby)
  attack_1: { id: 'attack_1', name: 'Scroll of Attack I', stat: 'attack', tier: 1, gemstone: 'ruby', icon: '📜' },
  attack_2: { id: 'attack_2', name: 'Scroll of Attack II', stat: 'attack', tier: 2, gemstone: 'ruby', icon: '📜' },
  attack_3: { id: 'attack_3', name: 'Scroll of Attack III', stat: 'attack', tier: 3, gemstone: 'ruby', icon: '📜' },
  attack_4: { id: 'attack_4', name: 'Scroll of Attack IV', stat: 'attack', tier: 4, gemstone: 'ruby', icon: '📜' },
  attack_5: { id: 'attack_5', name: 'Scroll of Attack V', stat: 'attack', tier: 5, gemstone: 'ruby', icon: '📜' },

  // Defense Scrolls (use Sapphire)
  defense_1: { id: 'defense_1', name: 'Scroll of Defense I', stat: 'defense', tier: 1, gemstone: 'sapphire', icon: '📜' },
  defense_2: { id: 'defense_2', name: 'Scroll of Defense II', stat: 'defense', tier: 2, gemstone: 'sapphire', icon: '📜' },
  defense_3: { id: 'defense_3', name: 'Scroll of Defense III', stat: 'defense', tier: 3, gemstone: 'sapphire', icon: '📜' },
  defense_4: { id: 'defense_4', name: 'Scroll of Defense IV', stat: 'defense', tier: 4, gemstone: 'sapphire', icon: '📜' },
  defense_5: { id: 'defense_5', name: 'Scroll of Defense V', stat: 'defense', tier: 5, gemstone: 'sapphire', icon: '📜' },

  // Health Scrolls (use Emerald)
  health_1: { id: 'health_1', name: 'Scroll of Health I', stat: 'health', tier: 1, gemstone: 'emerald', icon: '📜' },
  health_2: { id: 'health_2', name: 'Scroll of Health II', stat: 'health', tier: 2, gemstone: 'emerald', icon: '📜' },
  health_3: { id: 'health_3', name: 'Scroll of Health III', stat: 'health', tier: 3, gemstone: 'emerald', icon: '📜' },
  health_4: { id: 'health_4', name: 'Scroll of Health IV', stat: 'health', tier: 4, gemstone: 'emerald', icon: '📜' },
  health_5: { id: 'health_5', name: 'Scroll of Health V', stat: 'health', tier: 5, gemstone: 'emerald', icon: '📜' },

  // Crit Scrolls (use Topaz)
  critChance_1: { id: 'critChance_1', name: 'Scroll of Critical I', stat: 'critChance', tier: 1, gemstone: 'topaz', icon: '📜' },
  critChance_2: { id: 'critChance_2', name: 'Scroll of Critical II', stat: 'critChance', tier: 2, gemstone: 'topaz', icon: '📜' },
  critChance_3: { id: 'critChance_3', name: 'Scroll of Critical III', stat: 'critChance', tier: 3, gemstone: 'topaz', icon: '📜' },
  critChance_4: { id: 'critChance_4', name: 'Scroll of Critical IV', stat: 'critChance', tier: 4, gemstone: 'topaz', icon: '📜' },
  critChance_5: { id: 'critChance_5', name: 'Scroll of Critical V', stat: 'critChance', tier: 5, gemstone: 'topaz', icon: '📜' },

  // Speed Scrolls (use Amethyst)
  speed_1: { id: 'speed_1', name: 'Scroll of Swiftness I', stat: 'speed', tier: 1, gemstone: 'amethyst', icon: '📜' },
  speed_2: { id: 'speed_2', name: 'Scroll of Swiftness II', stat: 'speed', tier: 2, gemstone: 'amethyst', icon: '📜' },
  speed_3: { id: 'speed_3', name: 'Scroll of Swiftness III', stat: 'speed', tier: 3, gemstone: 'amethyst', icon: '📜' },
  speed_4: { id: 'speed_4', name: 'Scroll of Swiftness IV', stat: 'speed', tier: 4, gemstone: 'amethyst', icon: '📜' },
  speed_5: { id: 'speed_5', name: 'Scroll of Swiftness V', stat: 'speed', tier: 5, gemstone: 'amethyst', icon: '📜' },
};

// Scroll stat types for UI selection
export const SCROLL_STAT_TYPES = [
  { stat: 'attack', label: 'ATK', gemstone: 'ruby', icon: '⚔️' },
  { stat: 'defense', label: 'DEF', gemstone: 'sapphire', icon: '🛡️' },
  { stat: 'health', label: 'HP', gemstone: 'emerald', icon: '❤️' },
  { stat: 'critChance', label: 'CRIT', gemstone: 'topaz', icon: '💥' },
  { stat: 'speed', label: 'SPD', gemstone: 'amethyst', icon: '💨' },
];

// Scroll tiers for UI selection
export const SCROLL_TIERS = [1, 2, 3, 4, 5];

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/**
 * Get scroll ID from stat and tier
 * @param {string} stat - Stat type (attack, defense, health, critChance, speed)
 * @param {number} tier - Tier level (1-5)
 * @returns {string} Scroll ID
 */
export function getScrollId(stat, tier) {
  return `${stat}_${tier}`;
}

/**
 * Get scroll definition by stat and tier
 * @param {string} stat - Stat type
 * @param {number} tier - Tier level (1-5)
 * @returns {Object|null} Scroll definition
 */
export function getScrollByStatAndTier(stat, tier) {
  const scrollId = getScrollId(stat, tier);
  return SCROLL_TYPES[scrollId] || null;
}

/**
 * Format stat value for display
 * @param {string} stat - Stat type
 * @param {number} value - Stat value
 * @returns {string} Formatted string
 */
export function formatScrollStat(stat, value) {
  if (stat === 'critChance') {
    return `+${Math.round(value * 100)}%`;
  }
  return `+${value}`;
}

/**
 * Get stat label for display
 * @param {string} stat - Stat type
 * @returns {string} Display label
 */
export function getStatLabel(stat) {
  const labels = {
    attack: 'ATK',
    defense: 'DEF',
    health: 'HP',
    critChance: 'CRIT',
    speed: 'SPD',
  };
  return labels[stat] || stat;
}

export default {
  FORGING_CONFIG,
  GEMSTONES,
  GEMSTONE_IDS,
  SCROLL_TYPES,
  SCROLL_STAT_TYPES,
  SCROLL_TIERS,
  getScrollId,
  getScrollByStatAndTier,
  formatScrollStat,
  getStatLabel,
};
